import { Box, Button, Chip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useCallback } from "react";
import { InvoiceItemModel } from "types/generated/models";
import Row from "../../Shared/Row";
import { useClientPortalDispatch } from "../../../redux";
import { openInvoicePayStripeDialog } from "../invoiceSlice";
import { useAnalytics } from "hooks/useAnalytics";
import { useClientPortalConfigContext } from "context/ClientPortalConfig.ctx";
import { getServiceValue } from "../../../utils/serviceValue";

interface IInvoiceItemProps {
    invoice: InvoiceItemModel;
    hasUnpaidInvoices: boolean;
}

export default function InvoiceItem({ invoice, hasUnpaidInvoices }: IInvoiceItemProps) {
    const dispatch = useClientPortalDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { track } = useAnalytics();
    const { clientPortalConfig } = useClientPortalConfigContext();

    const effectiveStripeAccount = invoice.practitioner?.stripeAccount ?? clientPortalConfig.defaultStripeAccount;
    const hasStripePayment = !!effectiveStripeAccount;

    const downloadInvoice = useCallback(() => {
        if (!invoice.downloadInvoiceUrl) {
            return;
        }
        window.location.assign(invoice.downloadInvoiceUrl);
    }, [invoice]);

    const payInvoice = useCallback(() => {
        track("invoice_pay_clicked", {
            invoice_id: invoice.id,
            invoice_number: invoice.invoiceNumber,
            amount_owing: invoice.amountOwingDecimal,
        });
        dispatch(
            openInvoicePayStripeDialog({
                practitionerStripeAccountId: effectiveStripeAccount,
                practitionerId: invoice.practitioner?.practitionerId,
                invoiceIds: [invoice.id],
                amount: invoice.amountOwingDecimal,
                fee: invoice.fee,
            })
        );
    }, [dispatch, effectiveStripeAccount, invoice, track]);

    return (
        <Box
            sx={{
                border: "1px solid #D4D4D9",
                borderRadius: "4px",
                padding: "24px",
                width: "100%",
                minWidth: 0,
            }}
            data-test-id={`section_invoice_item_${invoice.invoiceNumber}`}
        >
            <Box sx={{ display: "flex", flexDirection: "row", gap: "8px", marginBottom: "8px" }}>
                <Typography variant="subtitle1" fontWeight="bold" data-test-id="text_invoice_date">
                    {invoice.invoiceDate}
                </Typography>
                <Typography variant="subtitle1" data-test-id="text_invoice_number">
                    {`Invoice #${invoice.invoiceNumber}`}
                    <Chip
                        sx={{ marginLeft: "8px" }}
                        label={invoice.isPaid ? "Paid" : "Unpaid"}
                        color={invoice.isPaid ? "success" : "default"}
                        size="small"
                        data-test-id="status_chip"
                    />
                </Typography>
            </Box>
            <Box
                sx={{
                    flexDirection: isMobile ? "column" : "row",
                    display: "flex",
                    gap: isMobile ? "24px" : "4px",
                    overflow: "hidden",
                    minWidth: 0,
                }}
            >
                <Box
                    sx={isMobile ? { width: "100%", minWidth: 0, overflow: "hidden" } : { flex: "1 1 0", minWidth: 0 }}
                >
                    <Row subtitle="Provider" value={invoice.practitionerName} dataTestIdSuffix="practitioner_name" />
                    <Row subtitle="Service" value={getServiceValue(invoice.services)} dataTestIdSuffix="service" wrap />
                    <Row subtitle="Client" value={invoice.clientName} dataTestIdSuffix="client_name" />
                </Box>
                <Box
                    sx={isMobile ? { width: "100%", minWidth: 0, overflow: "hidden" } : { flex: "1 1 0", minWidth: 0 }}
                >
                    <Row subtitle="Invoice Total" value={invoice.invoiceTotal} dataTestIdSuffix="invoice_total" />
                    <Row subtitle="Paid" value={invoice.amountPaid} dataTestIdSuffix="amount_paid" />
                    <Row subtitle="Owing" value={invoice.amountOwing} dataTestIdSuffix="amount_owing" />
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                        alignItems: isMobile ? "flex-start" : "flex-end",
                        gap: "8px",
                    }}
                >
                    {hasUnpaidInvoices && !invoice.isPaid && hasStripePayment && (
                        <Button
                            sx={{ width: isMobile ? "100%" : "100px" }}
                            size="extraSmall"
                            variant="contained"
                            onClick={payInvoice}
                            data-test-id="button_pay_invoice"
                        >
                            Pay
                        </Button>
                    )}
                    <Button
                        sx={{ width: isMobile ? "100%" : "100px" }}
                        size="extraSmall"
                        variant="outlined"
                        onClick={downloadInvoice}
                        data-test-id="button_download_invoice"
                    >
                        Download
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
